import {
  RiAddLine,
  RiAlertLine,
  RiArchiveLine,
  RiDownload2Line,
  RiEyeLine,
  RiFileCodeLine,
  RiFileTextLine,
  RiFingerprintLine,
  RiFullscreenExitLine,
  RiFullscreenLine,
  RiLayoutGridLine,
  RiLayoutLeft2Line,
  RiLayoutRight2Line,
  RiLinkM,
  RiLogoutBoxRLine,
  RiPrinterLine,
  RiLockPasswordLine,
  RiServerLine,
  RiShareForwardLine,
  RiTeamLine,
  RiUserLine,
  RiUserSettingsLine,
  RiShieldCheckLine,
  RiSpeedLine,
  RiTimeLine,
  RiUserStarLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Hint } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useStore, type HudVisibility } from '../state/store';
import { useAuthStore } from '../state/authStore';
import {
  CASE_ROLE_LABEL,
  CLASSIFICATION_LABEL,
  PRIORITY_LABEL,
  ROLE_LABEL,
} from '../state/domain';
import { buildCaseBundle, buildCaseReport, downloadText } from '@/lib/export';
import { cn, formatDay, shortId } from '@/lib/utils';

const HUD_LABELS: { key: keyof HudVisibility; label: string }[] = [
  { key: 'tools', label: '工具轨' },
  { key: 'controls', label: '视图控制' },
  { key: 'casePlate', label: '案件名牌' },
  { key: 'suggestions', label: '红绳建议' },
  { key: 'minimap', label: '鸟瞰图' },
  { key: 'bottomBar', label: '底部案情条' },
];

export function TopBar({
  onNewCase,
  onShare,
  onMembers,
  onUsers,
  onProviders,
  onChangePassword,
}: {
  onNewCase: () => void;
  onShare: () => void;
  onMembers: () => void;
  onUsers: () => void;
  onProviders: () => void;
  onChangePassword: () => void;
}) {
  const engagements = useStore(state => state.engagements);
  const activeEngagementId = useStore(state => state.activeEngagementId);
  const selectEngagement = useStore(state => state.selectEngagement);
  const meta = useStore(state => state.meta);
  const stats = useStore(state => state.stats);
  const panels = useStore(state => state.panels);
  const togglePanel = useStore(state => state.togglePanel);
  const focusWall = useStore(state => state.focusWall);
  const hud = useStore(state => state.hud);
  const toggleHud = useStore(state => state.toggleHud);
  const setAllHud = useStore(state => state.setAllHud);
  const setPreview = useStore(state => state.setPreview);
  const requestTour = useStore(state => state.requestTour);
  const toast = useStore(state => state.toast);
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);

  const wallReleased = !panels.left && !panels.right;

  const active = engagements.find(e => e.id === activeEngagementId);
  const expired = active?.expiresAt ? new Date(active.expiresAt).getTime() < Date.now() : false;

  const exportBundle = () => {
    const state = useStore.getState();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadText(`expotar-${active?.caseNo ?? 'case'}-${stamp}.json`, buildCaseBundle(state));
    toast('success', '卷宗 JSON 已导出');
  };

  const exportReport = () => {
    const state = useStore.getState();
    const stamp = new Date().toISOString().slice(0, 10);
    downloadText(`expotar-${active?.caseNo ?? 'case'}-${stamp}.md`, buildCaseReport(state));
    toast('success', '案情报告（Markdown）已导出');
  };

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-card px-3 shadow-[0_1px_2px_rgba(25,26,28,0.03)]">
      <div className="flex items-center gap-2">
        <div className="grid size-7 place-items-center rounded-[6px] bg-primary text-primary-foreground">
          <RiFingerprintLine className="size-4" />
        </div>
        <div className="leading-none">
          <div className="text-[12.5px] font-semibold tracking-[0.12em]">EXPOTAR</div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">情报研判工作台</div>
        </div>
      </div>

      <div className="h-6 w-px bg-border" />

      <Select value={activeEngagementId ?? ''} onValueChange={selectEngagement}>
        <SelectTrigger className="h-8 w-[280px]">
          <SelectValue placeholder="选择案件" />
        </SelectTrigger>
        <SelectContent>
          {engagements.map(e => (
            <SelectItem key={e.id} value={e.id}>
              {e.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {active && (
        <div className="flex items-center gap-1.5">
          <Badge variant="muted" className="font-mono">
            {active.caseNo ?? shortId(active.id, 10).toUpperCase()}
          </Badge>
          <Badge variant={active.classification === 'secret' || active.classification === 'confidential' ? 'cord' : 'outline'}>
            {CLASSIFICATION_LABEL[active.classification]}
          </Badge>
          {active.priority !== 'normal' && (
            <Badge variant="assess">优先级 {PRIORITY_LABEL[active.priority]}</Badge>
          )}
          {active.legalBasis && (
            <Hint label={`授权依据：${active.legalBasis}`}>
              <Badge variant="outline" className="gap-1">
                <RiShieldCheckLine className="size-3" />
                {active.legalBasis}
              </Badge>
            </Hint>
          )}
          {active.expiresAt && (
            <Hint label="授权到期时间">
              <Badge variant={expired ? 'cord' : 'outline'} className="gap-1">
                {expired ? <RiAlertLine className="size-3" /> : <RiTimeLine className="size-3" />}
                至 {formatDay(active.expiresAt)}
              </Badge>
            </Hint>
          )}
          <Badge variant={expired ? 'cord' : active.status === 'active' ? 'evidence' : 'muted'}>
            {expired ? '授权已过期' : active.status === 'active' ? '授权有效' : active.status}
          </Badge>
          {active.myRole && active.myRole !== 'owner' && (
            <Badge variant="outline">我的角色 {CASE_ROLE_LABEL[active.myRole]}</Badge>
          )}
        </div>
      )}

      <div className="flex-1" />

      {stats && (
        <div className="flex items-center gap-1">
          <Stat icon={RiUserStarLine} label="对象" value={stats.targets} />
          <Stat icon={RiLinkM} label="线索" value={stats.entities} />
          <Stat icon={RiArchiveLine} label="证据" value={stats.evidence} />
        </div>
      )}

      <Badge variant={meta?.ai.enabled ? 'lead' : 'muted'} className="gap-1">
        研判引擎 · {meta?.ai.enabled ? meta.ai.model : '本地'}
      </Badge>

      <div className="flex items-center gap-0.5 rounded-[var(--radius-sm)] border border-border p-0.5">
        <Hint label={`${panels.left ? '收起' : '展开'}案卷栏　[`}>
          <Button
            size="icon-sm"
            variant="ghost"
            className={cn(panels.left && 'bg-primary/10 text-primary')}
            onClick={() => togglePanel('left')}
          >
            <RiLayoutLeft2Line className="size-3.5" />
          </Button>
        </Hint>
        <Hint label={`${panels.right ? '收起' : '展开'}档案栏　]`}>
          <Button
            size="icon-sm"
            variant="ghost"
            className={cn(panels.right && 'bg-primary/10 text-primary')}
            onClick={() => togglePanel('right')}
          >
            <RiLayoutRight2Line className="size-3.5" />
          </Button>
        </Hint>
        <Hint label={wallReleased ? '还原侧栏　\\' : '释放关联墙（收起两侧）　\\'}>
          <Button
            size="icon-sm"
            variant="ghost"
            className={cn(wallReleased && 'bg-primary/10 text-primary')}
            onClick={focusWall}
          >
            {wallReleased ? <RiFullscreenExitLine className="size-3.5" /> : <RiFullscreenLine className="size-3.5" />}
          </Button>
        </Hint>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline">
            <RiLayoutGridLine className="size-3.5" />
            视图
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>显示浮层</DropdownMenuLabel>
          {HUD_LABELS.map(item => (
            <DropdownMenuCheckboxItem
              key={item.key}
              checked={hud[item.key]}
              onSelect={event => event.preventDefault()}
              onCheckedChange={() => toggleHud(item.key)}
            >
              {item.label}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setAllHud(!Object.values(hud).every(Boolean))}>
            <RiEyeLine className="size-3.5 text-muted-foreground" />
            {Object.values(hud).every(Boolean) ? '隐藏全部浮层' : '显示全部浮层'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setPreview(true)}>
            <RiFullscreenLine className="size-3.5 text-muted-foreground" />
            全屏预览（Shift+F）
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => requestTour()}>
            <RiSpeedLine className="size-3.5 text-muted-foreground" />
            自动巡航（全屏投屏）
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Hint label="全屏预览：隐藏全部界面，只留关联墙（Shift+F）">
        <Button size="sm" variant="outline" onClick={() => setPreview(true)}>
          <RiEyeLine className="size-3.5" />
          预览
        </Button>
      </Hint>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline">
            <RiDownload2Line className="size-3.5" />
            导出卷宗
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>导出</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => useStore.getState().setBriefOpen(true)}>
            <RiPrinterLine className="size-3.5 text-muted-foreground" />
            情报简报（打印 / 另存 PDF）
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportReport}>
            <RiFileTextLine className="size-3.5 text-muted-foreground" />
            案情报告（Markdown）
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={exportBundle}>
            <RiFileCodeLine className="size-3.5 text-muted-foreground" />
            完整卷宗（JSON）
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => window.print()}>
            <RiFileTextLine className="size-3.5 text-muted-foreground" />
            打印当前页
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button size="sm" onClick={onNewCase}>
        <RiAddLine className="size-3.5" />
        新建案件
      </Button>

      <div className="h-6 w-px bg-border" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-border bg-card px-1.5 py-1 pr-2 transition-colors hover:bg-accent"
          >
            <span className="grid size-6 place-items-center overflow-hidden rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="size-full object-cover" />
              ) : (
                (user?.name ?? '?').replace(/[^\p{L}\p{N}]/gu, '').slice(0, 1).toUpperCase()
              )}
            </span>
            <span className="hidden flex-col items-start leading-none md:flex">
              <span className="text-[11.5px] font-medium">{user?.name ?? '未登录'}</span>
              <span className="text-[9.5px] text-muted-foreground">
                {user ? ROLE_LABEL[user.role] : '—'}
              </span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[220px]">
          <DropdownMenuLabel>
            <div className="flex flex-col gap-0.5">
              <span className="text-[11.5px] font-medium normal-case tracking-normal text-foreground">
                {user?.name}
              </span>
              <span className="font-mono text-[10px] normal-case tracking-normal text-muted-foreground">
                {user?.email}
              </span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onMembers}>
            <RiTeamLine className="size-3.5 text-muted-foreground" />
            团队成员
          </DropdownMenuItem>
          {user?.role === 'admin' && (
            <>
              <DropdownMenuItem onSelect={onUsers}>
                <RiUserSettingsLine className="size-3.5 text-muted-foreground" />
                账号管理
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onProviders}>
                <RiServerLine className="size-3.5 text-muted-foreground" />
                AI 供应商
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onSelect={onShare}>
            <RiShareForwardLine className="size-3.5 text-muted-foreground" />
            共享演示链接
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPreview(true)}>
            <RiUserLine className="size-3.5 text-muted-foreground" />
            全屏预览
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onChangePassword}>
            <RiLockPasswordLine className="size-3.5 text-muted-foreground" />
            修改我的口令
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void logout()}>
            <RiLogoutBoxRLine className="size-3.5 text-muted-foreground" />
            退出登录
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-1 rounded-[5px] border border-border px-2 py-1">
      <Icon className="size-3 text-muted-foreground" />
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="font-mono text-[11px] font-medium">{value}</span>
    </div>
  );
}